import { Box, HStack, Text, VStack, Heading } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Trans } from '@lingui/react/macro';
import { FilterTags } from './FilterTags';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { djs } from '@duke-hq/djs';
import { DashboardStatBox } from './DashboardStatBox';
import duration from 'dayjs/plugin/duration';
import { constructQueryParams, stringifyValues } from '@duke-hq/lib';
import { useMemo } from 'react';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { ContractTypes } from '@duke-hq/shared-constants/enrolments';
import { t } from '@lingui/core/macro';
import FilterBarLayout from '@duke-hq/filters/components/FilterBarLayout';
import ContractTypeFilter from '@duke-hq/filters/components/ContractTypeFilter';
import CourseFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/CourseFilter';
import AssessorFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/AssessorFilter';
import ProvidersFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/ProvidersFilter';

djs.extend(duration);

function formatTime(hours: number): string {
  const dur = djs.duration(hours, 'hours');
  const h = Math.floor(dur.asHours());
  const m = dur.minutes();

  if (h === 0) {
    return `${m}m`;
  }

  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function AssessmentDashboard() {
  const { filters, updateFilters } = useFiltersContext();

  const courseIds = filters.courses?.length ? filters.courses.join(',') : undefined;
  const assessorIds = filters.assessorIds?.length ? filters.assessorIds.join(',') : undefined;
  const providerIds = filters.providerIds?.length ? filters.providerIds.join(',') : undefined;
  const contractIds = filters.contractIds?.length ? filters.contractIds.join(',') : undefined;

  const { data: assessors } = trpcAdmin.users.all.useQuery();
  const { data: courses } = trpcAdmin.courses.listCourses.useQuery({});
  const { data: providers } = trpcAdmin.providers.all.useQuery({});

  const assessorsOptions = useMemo(
    () => [
      { id: 'unassigned', label: 'Unassigned' },
      ...(assessors ?? []).map(({ id, first_name, last_name }) => ({
        id,
        label: `${first_name} ${last_name ?? ''}`.trim(),
      })),
    ],
    [assessors],
  );

  const coursesOptions = useMemo(
    () => (courses ?? []).map(({ id, title }) => ({ id, label: title })),
    [courses],
  );

  const providerOptions = useMemo(
    () => (providers ?? []).map(({ id, name }) => ({ id: id.toString(), label: name })),
    [providers],
  );

  const contractOptions = useMemo(
    () => ContractTypes.map(({ value, label }) => ({ id: value, label })),
    [],
  );

  const handleRemoveFilter = (filterKey: string, valueId: string) => {
    if (
      filterKey !== 'courses' &&
      filterKey !== 'assessorIds' &&
      filterKey !== 'providerIds' &&
      filterKey !== 'contractIds'
    ) {
      return;
    }
    updateFilters({ [filterKey]: (filters[filterKey] ?? []).filter((v) => v !== valueId) });
  };

  const { data: stats, isFetching } = trpcAdmin.assessments.dashboardStats.useQuery(
    stringifyValues({
      courseIds,
      assessorIds,
      providerIds,
      contractIds,
    }),
  );

  const hasNonUnassignedAssessor = useMemo(
    () => filters.assessorIds?.some((id) => id !== 'unassigned') ?? false,
    [filters.assessorIds],
  );

  const pendingUrl = `/assessments${constructQueryParams({
    outcome: 'PENDING',
    page: '1',
  })}`;

  const unassignedUrl = `/assessments${constructQueryParams({
    assessorIds: 'unassigned',
    outcome: 'PENDING',
    page: '1',
  })}`;

  return (
    <Box p={6} position="relative">
      <HStack mb={6} gap={8}>
        <Box flexShrink={0} whiteSpace="nowrap">
          <PageHeading>
            <Trans>Assessments Dashboard</Trans>
          </PageHeading>
        </Box>
        <FilterBarLayout
          filtersLabel={t`Filters`}
          clearAllLabel={t`Clear All`}
          moreFiltersLabel={t`More filters`}
        >
          <CourseFilter key="course" />
          <AssessorFilter key="assessor" />
          <ProvidersFilter key="provider" />
          <ContractTypeFilter key="contractType" />
        </FilterBarLayout>
      </HStack>

      <FilterTags
        filters={{
          courses: filters.courses,
          assessorIds: filters.assessorIds,
          providerIds: filters.providerIds,
          contractIds: filters.contractIds,
        }}
        coursesOptions={coursesOptions}
        assessorsOptions={assessorsOptions}
        providerOptions={providerOptions}
        contractOptions={contractOptions}
        onRemoveFilter={handleRemoveFilter}
      />

      <VStack align="stretch" spacing={6}>
        {/* Today Section */}
        <Box>
          <Heading size="sm" mb={4}>
            <Trans>Today</Trans>
          </Heading>
          <Box w="full" bg="bg.muted" p={4} borderRadius="md">
            <HStack spacing={4} justify="space-between">
              <DashboardStatBox
                label={<Trans>New Today</Trans>}
                value={stats?.today.newToday ?? 0}
                valueFontSize="sm"
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>Assessed</Trans>}
                value={stats?.today.assessed ?? 0}
                labelColor="green.500"
                valueFontSize="sm"
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>Pending</Trans>}
                value={stats?.today.pending ?? 0}
                labelColor="yellow.500"
                valueFontSize="sm"
                isLoading={isFetching}
                href={pendingUrl}
              />

              {!hasNonUnassignedAssessor && (
                <DashboardStatBox
                  label={<Trans>Unassigned</Trans>}
                  value={stats?.today.unassigned ?? 0}
                  labelColor="orange.500"
                  valueFontSize="sm"
                  isLoading={isFetching}
                  href={unassignedUrl}
                />
              )}
            </HStack>
          </Box>
        </Box>

        {/* Pending Section */}
        <Box>
          <Heading size="sm" mb={4}>
            <Trans>Pending</Trans>
          </Heading>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                <Trans>Total</Trans>
              </Text>
              <Box w="full" bg="bg.muted" p={4} borderRadius="md">
                <HStack spacing={4} justify="space-between">
                  <DashboardStatBox
                    label={<Trans>Pending</Trans>}
                    value={stats?.pending.byAttempt.total ?? 0}
                    labelColor="red.600"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label={<Trans>1st Attempt</Trans>}
                    value={stats?.pending.byAttempt.firstAttempt ?? 0}
                    labelColor="green.500"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label={<Trans>2nd Attempt</Trans>}
                    value={stats?.pending.byAttempt.secondAttempt ?? 0}
                    labelColor="yellow.500"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label={<Trans>3rd Attempt +</Trans>}
                    value={stats?.pending.byAttempt.thirdPlusAttempt ?? 0}
                    labelColor="orange.500"
                    isLoading={isFetching}
                  />
                </HStack>
              </Box>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                <Trans>Days elapsed</Trans>
              </Text>
              <Box w="full" bg="bg.muted" p={4} borderRadius="md">
                <HStack spacing={4} justify="space-between">
                  <DashboardStatBox
                    label="<1 day"
                    value={stats?.pending.byDaysElapsed.lessThan1Day ?? 0}
                    labelColor="green.500"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label="1-2 days"
                    value={stats?.pending.byDaysElapsed.oneTo2Days ?? 0}
                    labelColor="yellow.500"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label="2-5 days ago"
                    value={stats?.pending.byDaysElapsed.twoTo5Days ?? 0}
                    labelColor="orange.500"
                    isLoading={isFetching}
                  />

                  <DashboardStatBox
                    label="5 days +"
                    value={stats?.pending.byDaysElapsed.fivePlusDays ?? 0}
                    labelColor="red.600"
                    isLoading={isFetching}
                  />
                </HStack>
              </Box>
            </Box>
          </VStack>
        </Box>

        {/* Assessed Section */}
        <Box>
          <Heading size="sm" mb={4}>
            <Trans>Assessed</Trans>
          </Heading>
          <Box w="full" bg="bg.muted" p={4} borderRadius="md">
            <HStack spacing={4} justify="space-between" align="start">
              <DashboardStatBox
                label={<Trans>Last 7 days</Trans>}
                value={stats?.assessed.thisPeriod.count ?? 0}
                dateRange={
                  stats?.assessed.thisPeriod.dateRange.start
                    ? `${djs(stats.assessed.thisPeriod.dateRange.start).subtract(1, 'day').format('D')} - ${djs(stats.assessed.thisPeriod.dateRange.end).subtract(1, 'day').format('D MMM')}`
                    : undefined
                }
                percentChange={stats?.assessed.thisPeriod.percentChange}
                showLabelOutside
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>Previous 7 days</Trans>}
                value={stats?.assessed.prevPeriod.count ?? 0}
                dateRange={
                  stats?.assessed.prevPeriod.dateRange.start
                    ? `${djs(stats.assessed.prevPeriod.dateRange.start).subtract(1, 'day').format('D')} - ${djs(stats.assessed.prevPeriod.dateRange.end).subtract(1, 'day').format('D MMM')}`
                    : undefined
                }
                showLabelOutside
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>Past 30 Days</Trans>}
                value={stats?.assessed.past30Days ?? 0}
                showLabelOutside
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>All time</Trans>}
                value={stats?.assessed.allTime ?? 0}
                showLabelOutside
                isLoading={isFetching}
              />
            </HStack>
          </Box>
        </Box>

        {/* Response Time Section */}
        <Box>
          <Heading size="sm" mb={4}>
            <Trans>Response time</Trans>
          </Heading>
          <Box w="full" bg="bg.muted" p={4} borderRadius="md">
            <HStack spacing={4} justify="space-between">
              <DashboardStatBox
                label={<Trans>Last 7 days</Trans>}
                value={formatTime(stats?.responseTime.thisWeek.hours ?? 0)}
                percentChange={stats?.responseTime.thisWeek.percentChange}
                showLabelOutside
                isLoading={isFetching}
                invertPercentChangeColors
              />

              <DashboardStatBox
                label={<Trans>Previous 7 days</Trans>}
                value={formatTime(stats?.responseTime.lastWeek.hours ?? 0)}
                showLabelOutside
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>This Month</Trans>}
                value={formatTime(stats?.responseTime.thisMonth.hours ?? 0)}
                showLabelOutside
                isLoading={isFetching}
              />

              <DashboardStatBox
                label={<Trans>This year</Trans>}
                value={formatTime(stats?.responseTime.thisYear.hours ?? 0)}
                showLabelOutside
                isLoading={isFetching}
              />
            </HStack>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}
